<template>
  <aside
    class="tw:relative tw:flex tw:flex-col tw:justify-between tw:overflow-hidden tw:bg-panel-bg tw:p-14 tw:[@media(max-height:900px)]:p-10 tw:text-white"
    data-testid="auth-brand-panel">
    <div
      class="tw:pointer-events-none tw:absolute tw:-top-10 tw:-left-15 tw:size-[520px] tw:rounded-full tw:bg-[radial-gradient(circle_at_center,color-mix(in_srgb,var(--tw-color-brand-accent)_30%,transparent),transparent_68%)]"></div>
    <div
      class="tw:pointer-events-none tw:absolute tw:-right-40 tw:-bottom-45 tw:size-[520px] tw:rounded-full tw:bg-[radial-gradient(circle_at_center,color-mix(in_srgb,var(--tw-color-brand-accent)_18%,transparent),transparent_66%)]"></div>

    <div class="tw:relative tw:flex tw:items-center tw:gap-4">
      <img
        :src="brand.logos.panel"
        :alt="brand.name"
        class="tw:block tw:size-14 tw:object-contain tw:drop-shadow-[0_0_26px_color-mix(in_srgb,var(--tw-color-brand-accent)_45%,transparent)]"
        :class="{ 'tw:brightness-0 tw:invert': brand.logos.panelInvert }" />
      <BrandLogo tone="light" :size="28">
        <span class="tw:flex tw:flex-col tw:gap-[3px]">
          <span class="tw:text-20 tw:font-bold tw:tracking-[0.14em] tw:text-white">{{ brand.shortName }}</span>
          <span
            v-if="brand.byline"
            class="tw:text-11 tw:font-medium tw:tracking-[0.10em] tw:text-sidebar-muted tw:uppercase">
            by {{ brand.byline }}
          </span>
        </span>
      </BrandLogo>
    </div>

    <div class="tw:relative tw:flex tw:flex-col tw:gap-3.5">
      <div class="tw:text-[42px]/[50px] tw:font-bold tw:tracking-[-0.03em] tw:text-white tw:[text-wrap:pretty]">
        {{ headline }}
      </div>
      <div class="tw:max-w-[400px] tw:text-[17px]/[27px] tw:text-white/60 tw:[text-wrap:pretty]">
        {{ $t('auth.panel.description') }}
      </div>
    </div>

    <div
      class="tw:relative tw:h-[280px] tw:w-[312px] tw:self-end tw:[@media(max-height:820px)]:hidden"
      aria-hidden="true">
      <svg
        viewBox="0 0 392 280"
        width="392"
        height="280"
        fill="none"
        class="tw:absolute tw:inset-0 tw:overflow-visible">
        <defs>
          <linearGradient id="auth-spoke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" style="stop-color: var(--tw-color-brand-accent)" stop-opacity="0.85" />
            <stop offset="100%" style="stop-color: var(--tw-color-brand-accent)" stop-opacity="0.18" />
          </linearGradient>
        </defs>
        <g stroke="url(#auth-spoke)" stroke-width="1.5" stroke-linecap="round">
          <path v-for="y in SPOKES" :key="y" :d="spokePath(y)" />
        </g>
        <g
          class="auth-flow"
          style="stroke: var(--tw-color-brand-accent-lt)"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-dasharray="10 110">
          <path v-for="y in FLOWING_SPOKES" :key="y" :d="spokePath(y)" />
        </g>
        <circle
          cx="34"
          cy="140"
          r="30"
          style="fill: var(--tw-color-brand-accent); stroke: var(--tw-color-brand-accent)"
          fill-opacity="0.12"
          stroke-opacity="0.55"
          stroke-width="1.5" />
        <circle
          cx="34"
          cy="140"
          r="41"
          fill="none"
          style="stroke: var(--tw-color-brand-accent)"
          stroke-opacity="0.18"
          stroke-width="1" />
        <text x="34" y="144" text-anchor="middle" fill="#ffffff" class="tw:text-12 tw:font-bold tw:tracking-[0.12em]">
          {{ brand.emblem }}
        </text>
      </svg>
      <div
        class="tw:absolute tw:top-0 tw:left-[206px] tw:flex tw:h-[280px] tw:flex-col tw:items-start tw:justify-between">
        <span
          v-for="channel in CHANNELS"
          :key="channel.label ?? 'more'"
          class="tw:inline-flex tw:h-6 tw:items-center tw:rounded-full tw:whitespace-nowrap tw:border tw:px-[11px] tw:text-12 tw:font-medium"
          :class="{
            'tw:border-white/14 tw:bg-white/7 tw:text-white/85': channel.style === 'default',
            'tw:border-brand-accent/55 tw:bg-brand-accent/18 tw:text-white': channel.style === 'active',
            'tw:border-dashed tw:border-white/22 tw:bg-white/4 tw:text-white/60': channel.style === 'more',
          }">
          {{ channel.label ?? $t('auth.panel.many_more') }}
        </span>
      </div>
    </div>

    <div class="tw:relative tw:mt-auto tw:flex tw:flex-col">
      <div class="tw:h-px tw:bg-sidebar-border"></div>
      <template v-for="stat in STATS" :key="stat.value">
        <div class="tw:flex tw:items-baseline tw:justify-between tw:gap-6 tw:py-3">
          <span class="tw:text-[26px]/8 tw:font-bold tw:tracking-[-0.02em] tw:text-white">{{ stat.value }}</span>
          <span class="tw:text-right tw:text-13 tw:text-white/50">{{ $t(stat.labelKey) }}</span>
        </div>
        <div class="tw:h-px tw:bg-sidebar-border"></div>
      </template>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import translator from '@/locale/translator';
import { brand } from '@/config/brands';
import BrandLogo from '@/components/generic/BrandLogo.vue';

const headline = computed(() => translator.get(brand.panelHeadlineKey, { app: brand.shortName }));

const SPOKES = [12, 49, 85, 122, 158, 195, 231, 268];
const FLOWING_SPOKES = [12, 85, 158, 231, 268];

const CHANNELS = [
  { label: 'Instagram', style: 'default' },
  { label: 'TikTok', style: 'default' },
  { label: 'Facebook', style: 'default' },
  { label: 'YouTube', style: 'default' },
  { label: 'Google', style: 'active' },
  { label: 'LinkedIn', style: 'default' },
  { label: 'Spotify', style: 'default' },
  { label: null, style: 'more' },
];

const STATS = [
  { value: '25,000', labelKey: 'auth.panel.stat_roles' },
  { value: '95K+', labelKey: 'auth.panel.stat_campaigns' },
  { value: '20M', labelKey: 'auth.panel.stat_clicks' },
];

function spokePath(y: number) {
  return `M64 140 C 130 140, 140 ${y}, 206 ${y}`;
}
</script>

<style scoped>
@keyframes auth-flow {
  to {
    stroke-dashoffset: -120;
  }
}

.auth-flow {
  animation: auth-flow 3.2s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .auth-flow {
    animation: none;
  }
}
</style>
